import React, { useState } from 'react'
import { Play } from '../types'
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './ui/command'
import { cn } from '../lib/utils'

const CATEGORY_LABELS: Record<Play['category'], string> = {
  tragedy: 'Tragedies',
  comedy: 'Comedies',
  history: 'Histories',
  romance: 'Romances',
}

const CATEGORY_ORDER: Play['category'][] = ['tragedy', 'comedy', 'history', 'romance']

interface PlayComboboxProps {
  plays: Play[]
  value: string
  onChange: (playId: string) => void
  disabled?: boolean
}

export function PlayCombobox({ plays, value, onChange, disabled }: PlayComboboxProps) {
  const [open, setOpen] = useState(false)

  const selectedPlay = plays.find((p) => p.id === value)

  function handleSelect(playId: string) {
    onChange(playId)
    setOpen(false)
  }

  const grouped = CATEGORY_ORDER.reduce<Record<Play['category'], Play[]>>(
    (acc, cat) => {
      acc[cat] = plays.filter((p) => p.category === cat)
      return acc
    },
    { tragedy: [], comedy: [], history: [], romance: [] }
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            'w-full rounded-md border border-stone-700 bg-stone-800 px-3 py-2 text-sm text-left transition-colors',
            'focus:outline-none focus:ring-1 focus:ring-red-800',
            'disabled:cursor-not-allowed disabled:opacity-50',
            selectedPlay ? 'text-stone-100' : 'text-stone-500'
          )}
        >
          <span className="flex items-center justify-between gap-2">
            <span className="truncate">
              {selectedPlay ? selectedPlay.title : 'Select a play…'}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4 shrink-0 text-stone-400"
              aria-hidden="true"
            >
              <path d="m7 15 5 5 5-5" />
              <path d="m7 9 5-5 5 5" />
            </svg>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command
          filter={(itemValue, search) => {
            if (!search) return 1
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }}
        >
          <CommandInput placeholder="Search plays…" />
          <CommandList>
            <CommandEmpty>No plays found.</CommandEmpty>
            {CATEGORY_ORDER.map((cat) =>
              grouped[cat].length > 0 ? (
                <CommandGroup key={cat} heading={CATEGORY_LABELS[cat]}>
                  {grouped[cat].map((play) => (
                    <CommandItem
                      key={play.id}
                      value={play.title}
                      onSelect={() => handleSelect(play.id)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          value === play.id ? 'text-red-700' : 'opacity-0'
                        )}
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      {play.title}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
